import { Badge } from '@utilitywarehouse/hearth-react';
import { useEffect, useRef } from 'react';

import { FAQ } from '@components/FAQ';
import { useIsInView } from '@hooks/useIsInView';

import { faqSections } from './sections';
import './styles.scss';
import { Topics } from './types';

interface FAQProps {
  willCreditCheck: boolean;
  onFaqLinkClicked: (question: string) => void;
  onFaqHeaderDisplayed: () => void;
  excludeTopics?: Topics[];
}

const getShowAlternativeAnswer = (id: Topics, willCreditCheck: boolean) => {
  switch (id) {
    case Topics.creditCheck:
    case Topics.depositQuestioning: {
      return willCreditCheck;
    }

    default:
      return false;
  }
};

const mapSections = (willCreditCheck: boolean, excludeTopics: Topics[] = []) =>
  faqSections.map(({ title, topics, id }) => ({
    id,
    title,
    topics: topics
      .filter(({ id }) => !excludeTopics.includes(id))
      .map(
        ({
          id,
          question,
          defaultAnswer,
          conditionalAnswer,
          isPersonalized,
        }) => {
          const showAlternativeAnswer = getShowAlternativeAnswer(
            id,
            willCreditCheck
          );

          return {
            id,
            question,
            answer: (
              <div className="faq-answer-wrapper">
                <div className="faq-answer">
                  {showAlternativeAnswer ? conditionalAnswer : defaultAnswer}
                </div>
                {isPersonalized && (
                  <div className="faq-personalized-badge">
                    <Badge variant="subtle" colorScheme="positive" size="sm">
                      Answer personalised to you
                    </Badge>
                  </div>
                )}
              </div>
            ),
          };
        }
      ),
  }));

const EnergyFAQ = ({
  willCreditCheck,
  onFaqLinkClicked,
  onFaqHeaderDisplayed,
  excludeTopics,
}: FAQProps) => {
  const faqRef = useRef<HTMLDivElement>(null);
  const isInView = useIsInView(faqRef);

  useEffect(() => {
    if (isInView) {
      onFaqHeaderDisplayed();
    }
  }, [isInView, onFaqHeaderDisplayed]);

  const handleAccordionChanged = (question: string, isExpanded: boolean) => {
    if (isExpanded) {
      onFaqLinkClicked(question);
    }
  };

  return (
    <FAQ
      ref={faqRef}
      sections={mapSections(willCreditCheck, excludeTopics)}
      onAccordionChange={handleAccordionChanged}
    />
  );
};

export { EnergyFAQ };
